import dayjs from 'dayjs';
import i18next from 'i18next';

import { AddOnAssignment } from '~hooks/useAddOns/models';
import { Nullable } from '~types/Nullable';
import { toDecimal } from '~utils/decimal';

const EM_DASH = '—';

export const formatDate = (date: Nullable<dayjs.Dayjs | string>): string => {
  if (!date) return EM_DASH;
  // Validate via plain dayjs() before tz() — dayjs.tz() throws on invalid
  // input whereas dayjs() returns an invalid-but-non-throwing instance.
  if (typeof date === 'string' && !dayjs(date).isValid()) return EM_DASH;
  const d = dayjs.tz(date);
  return d.isValid() ? d.format('MMM DD, YYYY') : EM_DASH;
};

// Quantities print with thousands separators so a quote reads "1,000 Tons"
// rather than "1000 Tons".
export const formatQuantity = (quantity: number | string): string =>
  toDecimal(quantity).toNumber().toLocaleString(i18next.language);

// A fixed dollar amount for an add-on, or null when it has none. Percent-of-total
// and fuel-surcharge add-ons are priced against a base (total / fuel schedule)
// that has no quantity at quote time, so they carry no fixed amount and are
// excluded from the add-on subtotal.
export const computeAddOnFlatAmount = (addOn: AddOnAssignment): number | null => {
  if (addOn.isPercentOfTotal || addOn.isFuelSurcharge) return null;
  return toDecimal(addOn.rate).times(toDecimal(addOn.quantity)).toNumber();
};

export const COMBINED_RATE_MARKER = '*';

// A rate that folds a material sell price into the haul rate carries the
// marker, so the note below the table applies to those lines only — a quote
// where one row combines no longer reads as if every rate did.
export const formatRateWithMarker = (rate: string, isCombined: boolean): string => {
  if (!rate) return EM_DASH;
  return isCombined ? `${rate}${COMBINED_RATE_MARKER}` : rate;
};
